import { useState } from 'react';
import type { Experiment } from '@reelmachine/core';
import { useExperiments, useIsAdmin, useStopExperiment } from '../../api/learning';
import { daysSince, formatObjective } from '../../lib/learning-format';
import { useToast } from '../../ui/Toast';
import { QueryState } from './QueryState';

const OUTCOME: Record<Exclude<Experiment['status'], 'running'>, { label: string; cls: string }> = {
  won: { label: 'Won', cls: 'ok' },
  lost: { label: 'Lost', cls: 'warn' },
  inconclusive: { label: 'Inconclusive', cls: '' },
  stopped: { label: 'Stopped', cls: '' },
};

function Arms({ e }: { e: Experiment }) {
  const r = e.result;
  return (
    <table className="arms">
      <thead>
        <tr>
          <th scope="col">Arm</th>
          <th scope="col">Reels</th>
          <th scope="col">Reach vs channel</th>
        </tr>
      </thead>
      <tbody>
        {(['control', 'treatment'] as const).map((arm) => (
          <tr key={arm}>
            <th scope="row">
              {arm === 'control' ? 'Control' : 'Treatment'} (v
              {arm === 'control' ? e.controlVersion : e.treatmentVersion})
            </th>
            <td>{r ? r.n[arm] : 0}</td>
            <td>{r ? formatObjective(r.meanObjective[arm]) : 'n/a'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function RunningCard({ e, admin }: { e: Experiment; admin: boolean }) {
  const toast = useToast();
  const stop = useStopExperiment();
  const [confirming, setConfirming] = useState(false);
  const p = e.result?.probTreatmentBetter;
  const days = daysSince(e.startedAt);
  return (
    <article className="card" aria-label={e.hypothesis}>
      <span className="chip rec" style={{ alignSelf: 'flex-start' }}>
        Running
      </span>
      <h3>{e.hypothesis}</h3>
      <Arms e={e} />
      <div className="field">
        <span className="field-l" id={`p-${e.id}`}>
          Chance the treatment is better: {p === undefined ? 'n/a' : `${Math.round(p * 100)} %`}
        </span>
        <div
          className="track"
          role="progressbar"
          aria-labelledby={`p-${e.id}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={p === undefined ? undefined : Math.round(p * 100)}
        >
          <i style={{ width: `${Math.round((p ?? 0) * 100)}%` }} />
        </div>
      </div>
      <p className="nums">
        Day {days} of {e.maxDays} · at least {e.minPerArm} reels per arm · {Math.round(e.allocation * 100)} % get the
        treatment
      </p>
      {e.result?.note && <p className="small muted">{e.result.note}</p>}
      {admin &&
        (confirming ? (
          <div className="confirm" role="group" aria-label="Confirm stop">
            <p>Stop this experiment? New reels go back to the active playbook. This cannot be undone.</p>
            <div className="row nowrap">
              <button type="button" className="btn sec" onClick={() => setConfirming(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                disabled={stop.isPending}
                onClick={() =>
                  stop.mutate(e.id, {
                    onSuccess: () => toast('Experiment stopped'),
                    onError: (err) => toast(err.message),
                    onSettled: () => setConfirming(false),
                  })
                }
              >
                Stop experiment
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="btn sec" onClick={() => setConfirming(true)}>
            Stop
          </button>
        ))}
    </article>
  );
}

export function ExperimentsTab() {
  const q = useExperiments();
  const admin = useIsAdmin();
  return (
    <QueryState query={q}>
      {(all) => {
        const running = all.filter((e) => e.status === 'running');
        const ended = all.filter((e) => e.status !== 'running');
        return (
          <>
            <span className="label">Running ({running.length})</span>
            {running.length === 0 && <p className="muted">No experiment is running.</p>}
            {running.map((e) => (
              <RunningCard key={e.id} e={e} admin={admin} />
            ))}
            {ended.length > 0 && <span className="label">Ended</span>}
            {ended.map((e) => {
              const o = OUTCOME[e.status as keyof typeof OUTCOME];
              return (
                <article className="card" key={e.id} aria-label={e.hypothesis}>
                  <span className={`chip ${o.cls}`} style={{ alignSelf: 'flex-start' }}>
                    {o.label}
                  </span>
                  <h3>{e.hypothesis}</h3>
                  {e.result && <Arms e={e} />}
                  <p className="nums">
                    v{e.controlVersion} vs. v{e.treatmentVersion}
                    {e.endedAt ? ` · ended ${e.endedAt.slice(0, 10)}` : ''}
                  </p>
                </article>
              );
            })}
          </>
        );
      }}
    </QueryState>
  );
}
